import type { NarrativeValuationCompany } from '../../../hooks/useNarrativeValuationCompany';
import {
  calculateFutureRevenueAndEarnings,
  calculateProfitMarginPercentage,
} from '../lib';
import { useMemo } from 'react';
import { isNumber } from 'lodash';
import { getJsonField } from '@/utilities/object';
import type { NarrativeForm } from '@/pages/Narrative/utils/schema';

type PlotData = Record<number, { netIncome: number; revenue: number }>;

type Props = {
  company: NarrativeValuationCompany;
  values: NarrativeForm;
};

export const useTimeSeriesData = ({ company, values }: Props) => {
  const { profitMarginPercentage, revenuePercentage, timePeriod } = values;
  const { analysisPast, data } = company;
  const { revenue } = analysisPast ?? {};

  const { currencyInfo } = data;
  const reportingUnit =
    getJsonField<number>(currencyInfo, 'reporting_unit', isNumber) ?? 1;

  const currentYear = new Date().getFullYear();

  const { pastTimeRange, pastPlotData } = useMemo(() => {
    const netIncomeLtmHistory = analysisPast?.netIncomeLtmHistory ?? {};
    const revenueLtmHistory = analysisPast?.revenueLtmHistory ?? {};
    // Extract common keys from revenueLtmHistory and netIncomeLtmHistory
    const ltmHistoryKeys = Object.keys(netIncomeLtmHistory ?? {}).filter(
      (key) => key in revenueLtmHistory
    );

    // Combine revenueLtmHistory and netIncomeLtmHistory to an array
    const ltmHistory = ltmHistoryKeys.reduce<
      { netIncome: number; revenue: number; year: number }[]
    >(
      (acc, key) => [
        ...acc,
        {
          netIncome: netIncomeLtmHistory[key] * reportingUnit,
          revenue: revenueLtmHistory[key] * reportingUnit,
          year: currentYear - Math.abs(parseInt(key)),
        },
      ],
      []
    );

    const pastPlotData = ltmHistory.reduce<PlotData>(
      (acc, { netIncome, revenue, year }) => ({
        ...acc,
        [year]: { netIncome, revenue },
      }),
      {}
    );

    const pastTimeRange = ltmHistory.map(({ year }) => year);

    return {
      pastTimeRange,
      pastPlotData,
    };
  }, [currentYear, analysisPast, reportingUnit]);

  const { futureTimeRange, futurePlotData } = useMemo(() => {
    const futureTimeRange = new Array(timePeriod + 1)
      .fill(0)
      .map((_, index) => currentYear + index);

    const futurePlotData = futureTimeRange.reduce<PlotData>((result, year) => {
      const currentYearData = pastPlotData[currentYear];
      if (year === currentYear && currentYearData)
        return {
          ...result,
          [year]: currentYearData,
        };

      const profitMarginPercentageAtYear = calculateProfitMarginPercentage({
        timePeriod,
        futureProfitMarginPercentage: profitMarginPercentage,
        year: year - currentYear,
        presentProfitMarginPercentage: currentYearData
          ? (currentYearData.netIncome / currentYearData.revenue) * 100
          : 0,
      });

      const { futureNetIncome, futureRevenue } =
        calculateFutureRevenueAndEarnings({
          timePeriod: year - currentYear,
          profitMarginPercentage: profitMarginPercentageAtYear,
          reportingUnit,
          revenue: revenue ?? null,
          revenuePercentage,
        });

      return {
        ...result,
        [year]: { revenue: futureRevenue, netIncome: futureNetIncome },
      };
    }, {});

    return {
      futureTimeRange,
      futurePlotData,
    };
  }, [
    currentYear,
    profitMarginPercentage,
    reportingUnit,
    revenue,
    revenuePercentage,
    timePeriod,
    pastPlotData,
  ]);

  return useMemo(() => {
    // Plot data of past and future projections consumed by the chart
    const plotData = {
      ...pastPlotData,
      ...futurePlotData,
    };

    // Derive time range
    const timeRange = [...pastTimeRange, ...futureTimeRange];

    const valueRange = Object.values(plotData).reduce<number[]>(
      (acc, { netIncome, revenue }) => [...acc, revenue, netIncome],
      [0]
    );

    return {
      pastTimeRange,
      futureTimeRange,
      timeRange,
      valueRange,
      currentYear,
      plotData,
    };
  }, [
    currentYear,
    futurePlotData,
    futureTimeRange,
    pastPlotData,
    pastTimeRange,
  ]);
};
